import React, { useState, useEffect, useMemo, useCallback } from "react";
import { observer } from "mobx-react-lite";

import useThemeService from "../../../../services/theme-service/ThemeService";
import Button from "../../../../components/button/Button";
import { translationService } from "../../../../services/translation/TranslationService";
import { snackbarNotificationsService } from "../../../../services/notifications/SnackbarNotificationsService";
import { OrganizationSubscriptionPlansStorageModel } from "../../../../services/organization-subscription-plans/OrganizationSubscriptionPlansStorage";
import { api } from "../../../../services/api/ApiService";
import WarningBeforeAction from "../../../../components/data-components/WarningBeforeAction";
import useModal from "../../../../components/dialog/useModal";
import "../composition.scss";

export const PromotionCodeField = observer(() => {
  const { themeClass } = useThemeService();
  const [promoCode, setPromoCode] = useState("");
  const [currentCoupon, setCurrentCoupon] = useState("");
  const [errorKey, setErrorKey] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { toggleModal: toggleSubmitModal, isVisible: isSubmitModalVisible } = useModal();
  const { toggleModal: toggleDeleteModal, isVisible: isDeleteModalVisible } = useModal();

  const isSubmittingDisabled = useMemo(() => isSubmitting || !promoCode, [isSubmitting, promoCode]);

  const [organizationSubscriptionPlansStorage] = useState(() =>
    OrganizationSubscriptionPlansStorageModel.create({ autoLoad: false }, { api })
  );

  const fetchCurrentCoupon = useCallback(async () => {
    const code = await organizationSubscriptionPlansStorage.getCouponCode();
    setCurrentCoupon(code);
  }, [organizationSubscriptionPlansStorage]);

  useEffect(() => {
    fetchCurrentCoupon();
  }, [fetchCurrentCoupon, organizationSubscriptionPlansStorage]);

  const handlePromotionCodeChange = (event: any) => {
    setPromoCode(event.target.value);
    if (errorKey) setErrorKey("");
  };

  const handleSubmit = useCallback(async () => {
    try {
      if (currentCoupon) {
        toggleSubmitModal();
      }
      setIsSubmitting(true);
      if (errorKey) setErrorKey("");
      await organizationSubscriptionPlansStorage.setPromoCode(promoCode);
      setPromoCode("");
      await fetchCurrentCoupon();
      snackbarNotificationsService.showSuccess("BillingDetails.promotion-code.apply-success");
    } catch (error: any) {
      setErrorKey(error.errorKey);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    currentCoupon,
    errorKey,
    fetchCurrentCoupon,
    organizationSubscriptionPlansStorage,
    promoCode,
    toggleSubmitModal,
  ]);

  const toggleModalForSubmitting = useCallback(async () => {
    if (!currentCoupon) {
      await handleSubmit();
      return;
    }
    toggleSubmitModal();
  }, [currentCoupon, handleSubmit, toggleSubmitModal]);

  const handleDeleteConfirmationAction = useCallback(async () => {
    try {
      toggleDeleteModal();
      setIsSubmitting(true);
      if (errorKey) setErrorKey("");
      await organizationSubscriptionPlansStorage.removePromoCode();
      setCurrentCoupon("");
      snackbarNotificationsService.showSuccess("BillingDetails.promotion-code.delete-success");
    } catch (error: any) {
      setErrorKey(error.errorKey);
    } finally {
      setIsSubmitting(false);
    }
  }, [errorKey, organizationSubscriptionPlansStorage, toggleDeleteModal]);

  return (
    <>
      <span>{translationService.translate("BillingDetails.promotion")}</span>
      <div className="promotion-code">
        <input
          id="promotion-code"
          placeholder={translationService.translate("BillingDetails.promotion-code.placeholder")}
          value={promoCode}
          onChange={handlePromotionCodeChange}
          className={themeClass(["input"], ["discount"])}
        />
        <Button
          id="set-promotion"
          textKey="BillingDetails.promotion-code.set"
          className={themeClass(["btn-big"])}
          onClick={toggleModalForSubmitting}
          disabled={isSubmittingDisabled}
        />
        <WarningBeforeAction
          isVisible={isSubmitModalVisible}
          titleKey={"BillingDetails.promotion-code.apply-over-existing-coupon-modal-title"}
          titleContext={{ newPromotionCode: promoCode }}
          buttonOk={{
            id: "reset-ok",
            textKey: "BillingDetails.promotion-code.set",
            action: handleSubmit,
          }}
          toggleModal={toggleModalForSubmitting}
        >
          <div>
            {translationService.translate(
              "BillingDetails.promotion-code.apply-over-existing-coupon-modal-details-part-1"
            )}
            <span className="bold">{promoCode}</span>
            {translationService.translate(
              "BillingDetails.promotion-code.apply-over-existing-coupon-modal-details-part-2"
            )}
            <span className="bold">{currentCoupon}</span>
            {translationService.translate(
              "BillingDetails.promotion-code.apply-over-existing-coupon-modal-details-part-3"
            )}
          </div>
        </WarningBeforeAction>
      </div>

      <div className="error-message">{errorKey && translationService.translate(errorKey)}</div>

      {currentCoupon && (
        <div className="previous-code-message">
          <div>
            {translationService.translate("BillingDetails.promotion-code.applied")}
            <span className="bold">{currentCoupon}</span>
          </div>
          <Button
            id="delete-promotion"
            textKey="general.delete"
            className={themeClass(["btn-big", "fill-color-ghost"])}
            onClick={toggleDeleteModal}
          />
          <WarningBeforeAction
            isVisible={isDeleteModalVisible}
            titleKey={"BillingDetails.promotion-code.delete-title"}
            buttonOk={{
              id: "reset-ok",
              textKey: "general.delete",
              action: handleDeleteConfirmationAction,
            }}
            toggleModal={toggleDeleteModal}
          >
            <div>
              {translationService.translate("BillingDetails.promotion-code.delete-details-part-1")}
              <span className="bold">{currentCoupon}</span>
              {translationService.translate("BillingDetails.promotion-code.delete-details-part-2")}
            </div>
          </WarningBeforeAction>
        </div>
      )}
    </>
  );
});
